import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiCogOutline, mdiClockOutline, mdiChartBox, mdiRobotOutline, mdiMagnify, mdiFileChartOutline } from "@mdi/js";
import { MainLayout, ListSidebar } from "layouts";
import styles from "./styles.module.scss";
import { useAccount } from "shared/hooks";
import { Button } from "@mui/material";

type TaskStatus = "completed" | "in-progress" | "waiting";

interface TaskEntry {
	id: string;
	title: string;
	subtitle: string;
	status: TaskStatus;
	progress: number;
	agentName: string;
	agentAvatar?: string;
	ekeep: string;
	ekeepColor: string;
	startTime?: string;
	endTime?: string;
}

interface HistoryEntry {
	id: string;
	label: string;
	time: string;
	preview: string;
	isActive?: boolean;
}

const historyItems: HistoryEntry[] = [
	{ id: "1", label: "Hoje", time: "17:49", preview: "Atualização de KPIs..." },
	{ id: "2", label: "Ontem", time: "18:30", preview: "Relatório Semanal..." },
	{ id: "3", label: "09/03/2026", time: "15:20", preview: "Análise de Performance..." },
	{ id: "4", label: "08/03/2026", time: "09:15", preview: "Configuração de novos Agentes AI", isActive: true },
];

const taskEntries: TaskEntry[] = [
	{
		id: "1",
		title: "Atualizar Landing Page",
		subtitle: "Refatoração de CSS",
		status: "completed",
		progress: 100,
		agentName: "Pedro",
		ekeep: "Marketing",
		ekeepColor: "primary",
		startTime: "17:45",
		endTime: "17:49",
	},
	{
		id: "2",
		title: "Criar assets para redes sociais",
		subtitle: "Campanha de Inverno",
		status: "in-progress",
		progress: 65,
		agentName: "Antônio",
		ekeep: "Design",
		ekeepColor: "blue",
		startTime: "16:30",
	},
	{
		id: "3",
		title: "Qualificação de Leads B2B",
		subtitle: "Pipeline de Vendas",
		status: "waiting",
		progress: 0,
		agentName: "Andréa",
		ekeep: "Vendas",
		ekeepColor: "green",
	},
	{
		id: "4",
		title: "Revisão de Budget Mensal",
		subtitle: "Departamento Financeiro",
		status: "completed",
		progress: 100,
		agentName: "Juliana",
		ekeep: "Marketing",
		ekeepColor: "primary",
		startTime: "14:15",
		endTime: "15:02",
	},
];

const StatusIcon: React.FC<{ status: TaskStatus }> = ({ status }) => {
	switch (status) {
		case "completed":
			return (
				<Icon
					path={mdiCheckCircle}
					size={0.85}
					className={styles.statusCompleted}
				/>
			);
		case "in-progress":
			return (
				<span className={styles.statusSpinning}>
					<Icon
						path={mdiCogOutline}
						size={0.85}
						className={styles.statusInProgress}
					/>
				</span>
			);
		case "waiting":
			return (
				<Icon
					path={mdiClockOutline}
					size={0.85}
					className={styles.statusWaiting}
				/>
			);
	}
};

const formatTime = (entry: TaskEntry): string => {
	if (entry.status === "waiting") return "Aguardando";
	const start = entry.startTime ?? "--:--";
	const end = entry.endTime ?? "--:--";
	return `${start} | ${end}`;
};

export const ActivityLogPage = () => {
	const user = useAccount();
	const [search, setSearch] = useState("");

	const completedCount = taskEntries.filter((t) => t.status === "completed").length;
	const inProgressCount = taskEntries.filter((t) => t.status === "in-progress").length;
	const waitingCount = taskEntries.filter((t) => t.status === "waiting").length;

	const filteredHistory = historyItems.filter(
		(h) => h.label.toLowerCase().includes(search.toLowerCase()) || h.preview.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<MainLayout
			sidebar={
				<ListSidebar
					header={
						<div className={styles.sidebarHeader}>
							<div className={styles.sidebarHeaderIcon}>
								<Icon
									path={mdiRobotOutline}
									size={0.9}
								/>
							</div>
							<div>
								<h3 className={styles.sidebarHeaderTitle}>Minhas tarefas</h3>
								<p className={styles.sidebarHeaderSubtitle}>Histórico de atividades</p>
							</div>
						</div>
					}
					searchPlaceholder="Pesquisar histórico..."
					onSearch={setSearch}
					footer={
						<Button
							variant="outlined"
							color="primary"
							fullWidth
							className={styles.reportButton}
							disableElevation
							onClick={() => {}}
							startIcon={
								<Icon
									path={mdiFileChartOutline}
									size={0.8}
								/>
							}
						>
							Gerar relatório
						</Button>
					}
				>
					{filteredHistory.map((item) => (
						<div
							key={item.id}
							className={`${styles.historyCard} ${item.isActive ? styles.historyCardActive : ""}`}
						>
							<div className={styles.historyCardContent}>
								<div className={styles.historyCardHeader}>
									<span className={styles.historyCardLabel}>{item.label}</span>
									<span className={styles.historyCardTime}>{item.time}</span>
								</div>
								<p className={styles.historyCardPreview}>{item.preview}</p>
							</div>
						</div>
					))}
				</ListSidebar>
			}
		>
			<div className={styles.container}>
				{/* Header */}
				<header className={styles.header}>
					<div className={styles.headerLeft}>
						<div>
							<h2 className={styles.headerTitle}>Minhas tarefas</h2>
							{/* <div className={styles.headerStatus}>
								<span className={styles.pulseDot} />
								<span>Trabalhando...</span>
							</div> */}
						</div>
					</div>
					<div className={styles.headerStats}>
						<div className={styles.statItem}>
							<span className={styles.statLabel}>REALIZADAS HOJE</span>
							<span className={`${styles.statValue} ${styles.statCompleted}`}>{completedCount}</span>
						</div>
						<div className={styles.statItem}>
							<span className={styles.statLabel}>EM ANDAMENTO</span>
							<span className={`${styles.statValue} ${styles.statInProgress}`}>{inProgressCount}</span>
						</div>
						<div className={styles.statItem}>
							<span className={styles.statLabel}>AGUARDANDO</span>
							<span className={`${styles.statValue} ${styles.statWaiting}`}>{waitingCount}</span>
						</div>
					</div>
				</header>

				{/* Activity Log Table */}
				<div className={styles.tableWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Tarefa</th>
								<th>Progresso</th>
								<th>Agente</th>
								<th>Med.IA</th>
								<th>Início / Fim</th>
							</tr>
						</thead>
						<tbody>
							{taskEntries.map((entry) => (
								<tr key={entry.id}>
									<td>
										<p className={styles.taskTitle}>{entry.title}</p>
										<p className={styles.taskSubtitle}>{entry.subtitle}</p>
									</td>
									<td>
										<div className={styles.progressCell}>
											<div className={styles.progressBar}>
												<div
													className={`${styles.progressFill} ${styles[`progress-${entry.status}`]}`}
													style={{ width: `${entry.progress}%` }}
												/>
											</div>
											<StatusIcon status={entry.status} />
										</div>
									</td>
									<td>
										<div className={styles.agentCell}>
											<div className={styles.agentAvatar}>{entry.agentName.charAt(0)}</div>
											<span>{entry.agentName}</span>
										</div>
									</td>
									<td>
										<span className={`${styles.ekeepBadge} ${styles[`ekeep-${entry.ekeepColor}`]}`}>{entry.ekeep}</span>
									</td>
									<td>
										<span className={styles.timeText}>{formatTime(entry)}</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</MainLayout>
	);
};
